import { createContext, FC, ReactNode, useState } from 'react';
import { CashFlow, GroupedCashFlow } from '@algoan/dashboard/model';
import { CashFlowModal } from './cash-flow-modal';
import { isRecategorizationEnabledSelector, latestAnalysisSelector } from '@algoan/dashboard/store/selectors';
import { useSelector, useDispatch, Dispatch } from '@algoan/dashboard/store/store';
import { useDashboardParams } from '@algoan/dashboard/utils';
import { modifyCategoryAndUpdateAggregationData } from '@algoan/dashboard/store/thunks';
import { useModal } from '../global-modal/use-modal';

export interface UseRecategorizationModal {
  openModalWithCashFlow: (cashFlow: GroupedCashFlow | CashFlow) => void;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  RecategorizationModal: () => React.ReactElement;
}

export const OpenRecategorizationModalContext = createContext<Pick<UseRecategorizationModal, 'openModalWithCashFlow'>>({
  openModalWithCashFlow: () => null,
});

export const OpenRecategorizationModalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { openModalWithCashFlow, RecategorizationModal } = useRecategorizationModal();
  return (
    <>
      <OpenRecategorizationModalContext.Provider value={{ openModalWithCashFlow }}>
        {children}
      </OpenRecategorizationModalContext.Provider>
      <RecategorizationModal />
    </>
  );
};

export const useRecategorizationModal = (): UseRecategorizationModal => {
  const isRecategorizationEnabled = useSelector(isRecategorizationEnabledSelector);
  const { Modal } = useModal();

  const { customerId } = useDashboardParams();
  const analysis = useSelector(latestAnalysisSelector);
  const analysisId = analysis?.id;

  const [displayModal, setDisplayModal] = useState(false);
  const [cashFlow, setCashFlow] = useState<GroupedCashFlow | CashFlow | null>(null);

  const closeModal = (): void => {
    setDisplayModal(false);
  };

  const openModalWithCashFlow = (cashFlow: GroupedCashFlow | CashFlow): void => {
    setCashFlow(cashFlow);
    setDisplayModal(true);
  };

  const dispatch: Dispatch = useDispatch();

  const RecategorizationModal = (): React.ReactElement => (
    <>
      {isRecategorizationEnabled && (
        <Modal open={displayModal} onClose={closeModal} id={`${cashFlow?.id}-${cashFlow?.category}`}>
          {cashFlow && (
            <CashFlowModal
              cashFlow={cashFlow}
              onClose={closeModal}
              isModalOpen={displayModal}
              analysisId={analysisId}
              customerId={customerId}
              onChangeCategory={async (selectedCategory) => {
                customerId &&
                  analysisId &&
                  (await dispatch(
                    modifyCategoryAndUpdateAggregationData(customerId, analysisId, cashFlow, selectedCategory),
                  ));
              }}
            />
          )}
        </Modal>
      )}
    </>
  );

  return {
    openModalWithCashFlow,
    RecategorizationModal,
  };
};
